import { NgClass } from "@angular/common";
import { Component, ElementRef, HostListener, ViewChild } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CollectedPreviewItem, GameType, SearchTermType, SimpleSearchRequest, SimpleSearchResponse, SimpleSearchTerm } from "@apiserver-rest/ponder-data-client-base";
import { getFromLocalStorage, LocalStorageKey } from "@ponder-shared/utils/localStorage";
import { debounceTime, distinctUntilChanged, fromEvent, map, Subscription, take } from "rxjs";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { gameTypeToGameInfo } from "@ponder-shared/utils/gameInfo";
import { NavigationService } from "@ponder-core/navigation/navigation.service";

@Component({
    selector: "primary-search",
    imports: [ReactiveFormsModule, NgClass],
    templateUrl: "./primary-search.component.html"
})
export class PrimarySearchComponent {
    public primarySearchFormGroup = new FormGroup({
        query: new FormControl("", [ 
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(64)
        ])},
        { updateOn: 'change' });

    @ViewChild("primarySearch") primarySearchRef!: ElementRef<HTMLInputElement>;
    @ViewChild("searchSuggestionsContainer") searchSuggestionsContainerRef!: ElementRef<HTMLElement>;

    public readonly searchSvgIcon: string = SvgIconMap.get(SvgIcon.Search)!;

    public shouldShow: boolean = false;
    public searchSuggestions?: CollectedPreviewItem[];
    public searchTerms?: SimpleSearchTerm[];
    
    private searchSub!: Subscription;
    private isSearching: boolean = false;
    private lastSearch?: string;

    public constructor(private ponderDataClient: PonderDataClient, private navigation: NavigationService) {

    }

    public ngAfterViewInit(): void {
        this.searchSub = fromEvent(this.primarySearchRef.nativeElement, 'input')
            .pipe(map((event: Event) => (event.target as HTMLInputElement).value))
            .pipe(debounceTime(750))
            .pipe(distinctUntilChanged())
            .subscribe({
                next: (value: string) => {
                    this.lastSearch = value.toLowerCase();
                    this.onInputChange(value);
                }
            });
    }

    public ngOnDestroy(): void {
        this.searchSub.unsubscribe();
    }

    public onInputFocus(): void {
        if (this.searchTerms?.length) {
            this.shouldShow = true;
        }
    }
    
    protected onClose(): void {
        this.shouldShow = false;
    }

    public onSubmit(): void {
        var searchControl = this.primarySearchFormGroup.controls["query"];
        var game = getFromLocalStorage(LocalStorageKey.ActiveGameSelection, GameType.Magic)! as GameType;
        
        if (searchControl.valid) {
            var request = new SimpleSearchRequest({ term: searchControl.value! });

            this.ponderDataClient
                .searchSimple(request)
                .pipe(take(1))
                .subscribe({
                    next: (response: SimpleSearchResponse) => {
                        if (response.success) {
                            if (response.results?.length) {
                                if (this.isExactMatch(response.results[0])) {
                                    this.navigation.goto(response.results[0].navigatesTo!);
                                } else {
                                    this.navigation.goto("/search", { game: game, name: searchControl.value! });
                                }
                            } else {
                                this.navigation.goto("/search", { game: game, name: searchControl.value! });
                            }
                        }
                    }
                });
        }
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if (this.shouldShow && !this.searchSuggestionsContainerRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (this.shouldShow && this.mouseDownOutside && !this.searchSuggestionsContainerRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }

    // -------------------------------------------------------------------------------------
    // Actions
    // -------------------------------------------------------------------------------------

    private onInputChange(value: string): void {
        if (this.isSearching) {
            return;
        }

        this.isSearching = true;
        this.searchTerms = undefined;

        var request = new SimpleSearchRequest({ term: value });

        this.ponderDataClient
            .searchSimple(request)
            .pipe(take(1))
            .subscribe({
                next: (response: SimpleSearchResponse) => {
                    this.searchTerms = response.results;
                    this.shouldShow = true;
                }
            })
            .add(() => { this.isSearching = false; });
    }

    public isExactMatch(searchTerm: SimpleSearchTerm): boolean {
        return (this.lastSearch && (this.lastSearch === searchTerm.term?.toLowerCase()) ? true : false);
    }

    public getTermType(searchTerm: SimpleSearchTerm): string {
        var gameInfo = gameTypeToGameInfo(searchTerm.game);
        var gameStr = (gameInfo ? `${gameInfo.shortName} - ` : "");
        var termStr = (searchTerm.searchTermType == SearchTermType.Item) ? searchTerm.itemType : searchTerm.searchTermType;
        
        return gameStr + termStr;
    }
}